export const DEFAULT_DATE_TIME_FALLBACK = '—'
export const DEFAULT_FITRO_TIME_ZONE = 'Europe/Warsaw'

export type LocalDateString = string & {readonly __localDate: unique symbol}
export type IanaTimeZone = string & {readonly __ianaTimeZone: unique symbol}

export type LocalDateParts = Readonly<{
  year: number
  month: number
  day: number
}>

export type LocalTimeParts = Readonly<{
  hour: number
  minute: number
  second: number
}>

export type DateStyle = 'numeric' | 'dayMonth' | 'long'
export type WeekdayWidth = 'narrow' | 'short' | 'long'
export type LabelContext = 'inline' | 'sentence'
export type LocalDateFormatOptions = Pick<Intl.DateTimeFormatOptions, 'day' | 'month' | 'year' | 'weekday' | 'dateStyle'>
export type InstantFormatOptions = Omit<Intl.DateTimeFormatOptions, 'timeZone'>
export type RangeLabelOptions = Readonly<{separator?: 'compact' | 'spaced' | 'hyphen'; collapseSameDate?: boolean; collapseSharedParts?: boolean; omitRepeatedYear?: boolean}>

export type DateTimeFormatterConfig = Readonly<{
  locale: string
  timeZone: string
  fallback?: string
  now?: () => Date
}>

export type DateTimeFormatter = Readonly<{
  locale: string
  timeZone: string
  fallback: string
  formatLocalDateWithOptions: (value: string | null | undefined, options: LocalDateFormatOptions) => string
  formatInstantWithOptions: (value: Date | string | null | undefined, options: InstantFormatOptions) => string
  formatLocalDateRangeWithOptions: (start: string | null | undefined, end: string | null | undefined, options: LocalDateFormatOptions, endFallback?: string, rangeOptions?: RangeLabelOptions) => string
  formatInstantRangeWithOptions: (start: Date | string | null | undefined, end: Date | string | null | undefined, options: InstantFormatOptions, endOptions?: InstantFormatOptions, rangeOptions?: Pick<RangeLabelOptions, 'separator' | 'collapseSharedParts'>) => string
  formatLocalDate: (
    value: string | null | undefined,
    options?: Readonly<{style?: DateStyle; includeYear?: boolean}>,
  ) => string
  formatLocalDateWithWeekday: (
    value: string | null | undefined,
    options?: Readonly<{
      dateStyle?: DateStyle
      weekday?: WeekdayWidth
      context?: LabelContext
      includeYear?: boolean
    }>,
  ) => string
  formatLocalDateDay: (value: string | null | undefined) => string
  formatLocalDateWeekday: (
    value: string | null | undefined,
    options?: Readonly<{width?: WeekdayWidth; context?: LabelContext}>,
  ) => string
  formatLocalDateRange: (
    start: string | null | undefined,
    end: string | null | undefined,
    options?: Readonly<{style?: Exclude<DateStyle, 'dayMonth'>}>,
  ) => string
  formatLocalDateRangeWithWeekday: (
    start: string | null | undefined,
    end: string | null | undefined,
    options?: Readonly<{
      style?: Exclude<DateStyle, 'dayMonth'>
      weekday?: WeekdayWidth
      context?: LabelContext
    }>,
  ) => string
  formatLocalTime: (
    value: string | null | undefined,
    options?: Readonly<{seconds?: boolean}>,
  ) => string
  formatLocalDateTime: (
    date: string | null | undefined,
    time: string | null | undefined,
    options?: Readonly<{dateStyle?: Exclude<DateStyle, 'dayMonth'>; seconds?: boolean}>,
  ) => string
  formatDayBoundaryTime: (value: string | null | undefined) => string
  formatTimeRange: (
    start: string | null | undefined,
    end: string | null | undefined,
    options?: Readonly<{seconds?: boolean}>,
  ) => string
  formatInstantTimeRange: (
    start: Date | string | null | undefined,
    end: Date | string | null | undefined,
    options?: Readonly<{seconds?: boolean}>,
  ) => string
  formatDayBoundaryTimeRange: (
    start: string | null | undefined,
    end: string | null | undefined,
  ) => string
  formatInstantDate: (
    value: Date | string | null | undefined,
    options?: Readonly<{style?: DateStyle}>,
  ) => string
  formatInstantTime: (
    value: Date | string | null | undefined,
    options?: Readonly<{seconds?: boolean}>,
  ) => string
  formatInstantDateTime: (
    value: Date | string | null | undefined,
    options?: Readonly<{seconds?: boolean; dateStyle?: Exclude<DateStyle, 'dayMonth'>}>,
  ) => string
  formatInstantRange: (
    start: Date | string | null | undefined,
    end: Date | string | null | undefined,
    options?: Readonly<{seconds?: boolean}>,
  ) => string
  formatInstantSession: (value: Readonly<{
    start: Date | string | null | undefined
    end?: Date | string | null
    dateStyle?: Exclude<DateStyle, 'dayMonth'>
    includeYear?: boolean
    seconds?: boolean
  }>) => Readonly<{
    timeLabel: string
    dateLabel: string
    combinedLabel: string
    isCrossDay: boolean
  }>
  formatOffsetSession: (value: Readonly<{
    start: string | null | undefined
    end?: string | null
    dateStyle?: Exclude<DateStyle, 'dayMonth'>
    includeYear?: boolean
  }>) => Readonly<{
    timeLabel: string
    dateLabel: string
    combinedLabel: string
    isCrossDay: boolean
  }>
  formatSession: (value: Readonly<{
    localDate: string | null | undefined
    localEndDate?: string | null
    localStartTime: string | null | undefined
    localEndTime?: string | null
    dateStyle?: Exclude<DateStyle, 'dayMonth'>
    includeYear?: boolean
  }>) => Readonly<{
    timeLabel: string
    dateLabel: string
    combinedLabel: string
    isCrossDay: boolean
  }>
  formatWeekday: (
    isoWeekday: number,
    options?: Readonly<{width?: WeekdayWidth; context?: LabelContext}>,
  ) => string
  formatWeekdayRange: (
    startIsoWeekday: number,
    endIsoWeekday: number,
    options?: Readonly<{width?: WeekdayWidth; context?: LabelContext}>,
  ) => string
  formatWeekdayTime: (
    isoWeekday: number,
    time: string | null | undefined,
    options?: Readonly<{weekday?: WeekdayWidth; context?: LabelContext}>,
  ) => string
  formatMonthTitle: (
    value: string | null | undefined,
    options?: Readonly<{includeYear?: boolean; context?: LabelContext}>,
  ) => string
  formatLocalDateMonthTitle: (
    value: string | null | undefined,
    options?: Readonly<{includeYear?: boolean; context?: LabelContext}>,
  ) => string
  formatRelative: (value: Date | string | null | undefined) => string
  today: () => LocalDateString
  dateKeyInZone: (value: Date | string | null | undefined) => LocalDateString | null
  isSameDayInZone: (
    first: Date | string | null | undefined,
    second: Date | string | null | undefined,
  ) => boolean
}>

const LOCAL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/u
const LOCAL_TIME_PATTERN = /^(\d{2}):(\d{2})(?::(\d{2}))?$/u
const YEAR_MONTH_PATTERN = /^(\d{4})-(\d{2})$/u
const OFFSET_PATTERN = /(?:Z|[+-]\d{2}:\d{2})$/u
const INSTANT_PATTERN = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/u
const EN_DASH = '–'
const UTC = 'UTC'

const formatterCache = new Map<string, Intl.DateTimeFormat>()
const relativeFormatterCache = new Map<string, Intl.RelativeTimeFormat>()

const formatter = (
  locale: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat => {
  const key = `${locale}|${JSON.stringify(options)}`
  const cached = formatterCache.get(key)
  if (cached) return cached
  const created = new Intl.DateTimeFormat(locale, options)
  formatterCache.set(key, created)
  return created
}

const relativeFormatter = (locale: string): Intl.RelativeTimeFormat => {
  const cached = relativeFormatterCache.get(locale)
  if (cached) return cached
  const created = new Intl.RelativeTimeFormat(locale, {numeric: 'auto'})
  relativeFormatterCache.set(locale, created)
  return created
}

const capitalize = (value: string, locale: string): string => {
  if (!value) return value
  return `${value.charAt(0).toLocaleUpperCase(locale)}${value.slice(1)}`
}

const asUtcCalendarDate = ({year, month, day}: LocalDateParts): Date =>
  new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0))

const localDateFromParts = ({year, month, day}: LocalDateParts): LocalDateString =>
  `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day
    .toString()
    .padStart(2, '0')}` as LocalDateString

export const parseLocalDate = (
  value: string | null | undefined,
): LocalDateParts | null => {
  const match = value?.trim().match(LOCAL_DATE_PATTERN)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const candidate = asUtcCalendarDate({year, month, day})
  if (
    candidate.getUTCFullYear() !== year ||
    candidate.getUTCMonth() + 1 !== month ||
    candidate.getUTCDate() !== day
  ) {
    return null
  }
  return {year, month, day}
}

export const parseLocalTime = (
  value: string | null | undefined,
): LocalTimeParts | null => {
  const match = value?.trim().match(LOCAL_TIME_PATTERN)
  if (!match) return null
  const hour = Number(match[1])
  const minute = Number(match[2])
  const second = Number(match[3] ?? 0)
  if (hour > 23 || minute > 59 || second > 59) return null
  return {hour, minute, second}
}

export const parseInstant = (
  value: Date | string | null | undefined,
): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : new Date(value.getTime())
  }
  const normalized = value?.trim()
  if (!normalized || !normalized.includes('T') || !OFFSET_PATTERN.test(normalized)) {
    return null
  }
  const components = normalized.match(INSTANT_PATTERN)
  if (!components || !parseLocalDate(components[1])) return null
  const hour = Number(components[2])
  const minute = Number(components[3])
  const second = Number(components[4] ?? 0)
  if (hour > 23 || minute > 59 || second > 59) return null
  const date = new Date(normalized)
  return Number.isNaN(date.getTime()) ? null : date
}

const offsetLocalParts = (value: string | null | undefined) => {
  const normalized = value?.trim()
  if (!normalized || !parseInstant(normalized)) return null
  const components = normalized.match(INSTANT_PATTERN)
  if (!components) return null
  return {
    localDate: components[1],
    localTime: `${components[2]}:${components[3]}:${components[4] ?? '00'}`,
  }
}

export const isIanaTimeZone = (value: unknown): value is IanaTimeZone => {
  if (typeof value !== 'string' || !value.trim() || /^[+-]/u.test(value)) return false
  try {
    formatter('en', {timeZone: value, year: 'numeric'}).format(new Date(0))
    return true
  } catch {
    return false
  }
}

const formatCalendarDate = (
  locale: string,
  parts: LocalDateParts,
  style: DateStyle,
  includeYear: boolean,
): string => {
  const date = asUtcCalendarDate(parts)
  if (style === 'long') {
    return formatter(locale, {
      day: 'numeric',
      month: 'long',
      ...(includeYear ? {year: 'numeric'} : {}),
      timeZone: UTC,
    }).format(date)
  }
  return formatter(locale, {
    day: 'numeric',
    month: '2-digit',
    ...(style === 'numeric' && includeYear ? {year: 'numeric'} : {}),
    timeZone: UTC,
  }).format(date)
}

const formatMonthInDateContext = (
  locale: string,
  parts: LocalDateParts,
): string => {
  const date = asUtcCalendarDate(parts)
  const month = formatter(locale, {
    day: 'numeric',
    month: 'long',
    timeZone: UTC,
  })
    .formatToParts(date)
    .find(part => part.type === 'month')?.value
  return month ?? formatter(locale, {month: 'long', timeZone: UTC}).format(date)
}

const sameLocalDate = (first: LocalDateParts, second: LocalDateParts): boolean =>
  first.year === second.year && first.month === second.month && first.day === second.day

const formatPolishNumericRange = (
  start: LocalDateParts,
  end: LocalDateParts,
): string => {
  if (sameLocalDate(start, end)) return `${start.day}.${start.month.toString().padStart(2, '0')}.${start.year}`
  if (start.year === end.year && start.month === end.month) {
    return `${start.day}${EN_DASH}${end.day}.${end.month.toString().padStart(2, '0')}.${end.year}`
  }
  if (start.year === end.year) {
    return `${start.day}.${start.month.toString().padStart(2, '0')}${EN_DASH}${end.day}.${end.month
      .toString()
      .padStart(2, '0')}.${end.year}`
  }
  return `${start.day}.${start.month.toString().padStart(2, '0')}.${start.year}${EN_DASH}${end.day}.${end.month
    .toString()
    .padStart(2, '0')}.${end.year}`
}

const formatLongRange = (
  locale: string,
  start: LocalDateParts,
  end: LocalDateParts,
): string => {
  if (!locale.toLocaleLowerCase().startsWith('pl')) {
    return formatter(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: UTC,
    }).formatRange(asUtcCalendarDate(start), asUtcCalendarDate(end))
  }
  if (sameLocalDate(start, end)) return formatCalendarDate(locale, start, 'long', true)
  if (start.year === end.year && start.month === end.month) {
    return `${start.day}${EN_DASH}${end.day} ${formatMonthInDateContext(locale, end)} ${end.year}`
  }
  if (start.year === end.year) {
    return `${start.day} ${formatMonthInDateContext(locale, start)} ${EN_DASH} ${end.day} ${formatMonthInDateContext(
      locale,
      end,
    )} ${end.year}`
  }
  return `${formatCalendarDate(locale, start, 'long', true)} ${EN_DASH} ${formatCalendarDate(
    locale,
    end,
    'long',
    true,
  )}`
}

const localDatePartsInZone = (
  locale: string,
  date: Date,
  timeZone: string,
): LocalDateParts | null => {
  const parts = formatter(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone,
  }).formatToParts(date)
  const value = (type: Intl.DateTimeFormatPartTypes): number | null => {
    const part = parts.find(candidate => candidate.type === type)?.value
    if (!part) return null
    const numeric = Number(part)
    return Number.isInteger(numeric) ? numeric : null
  }
  const year = value('year')
  const month = value('month')
  const day = value('day')
  return year && month && day ? {year, month, day} : null
}

export const createDateTimeFormatter = (
  config: DateTimeFormatterConfig,
): DateTimeFormatter => {
  if (!config.locale.trim()) throw new RangeError('Date-time locale must not be empty.')
  if (!isIanaTimeZone(config.timeZone)) {
    throw new RangeError(`Invalid IANA time zone: ${config.timeZone}`)
  }

  const locale = config.locale
  const timeZone = config.timeZone
  const fallback = config.fallback ?? DEFAULT_DATE_TIME_FALLBACK
  const now = config.now ?? (() => new Date())

  const formatLocalDateWithOptions: DateTimeFormatter['formatLocalDateWithOptions'] = (value, options) => {
    const parts = parseLocalDate(value)
    return parts ? formatter(locale, {...options, timeZone: UTC}).format(asUtcCalendarDate(parts)) : fallback
  }
  const formatInstantWithOptions: DateTimeFormatter['formatInstantWithOptions'] = (value, options) => {
    const instant = parseInstant(value)
    return instant ? formatter(locale, {...options, timeZone}).format(instant) : fallback
  }
  const formatLocalDateRangeWithOptions: DateTimeFormatter['formatLocalDateRangeWithOptions'] = (start, end, options, endFallback = fallback, rangeOptions) =>
    formatLocalDateRangeWithOptionsInLocale(start, end, locale, options, endFallback, rangeOptions, fallback)
  const formatInstantRangeWithOptions: DateTimeFormatter['formatInstantRangeWithOptions'] = (start, end, options, endOptions = options, rangeOptions) => {
    const first = parseInstant(start), last = parseInstant(end)
    if (rangeOptions?.collapseSharedParts && first && last) {
      return formatter(locale, {...options, timeZone}).formatRange(first, last)
    }
    return `${formatInstantWithOptions(start, options)}${rangeSeparator(rangeOptions)}${formatInstantWithOptions(end, endOptions)}`
  }

  const formatLocalDate: DateTimeFormatter['formatLocalDate'] = (value, options = {}) => {
    const parts = parseLocalDate(value)
    if (!parts) return fallback
    return formatCalendarDate(
      locale,
      parts,
      options.style ?? 'numeric',
      options.includeYear ?? options.style !== 'dayMonth',
    )
  }

  const formatWeekday: DateTimeFormatter['formatWeekday'] = (isoWeekday, options = {}) => {
    if (!Number.isInteger(isoWeekday) || isoWeekday < 1 || isoWeekday > 7) return fallback
    const date = new Date(Date.UTC(2024, 0, isoWeekday, 12))
    const label = formatter(locale, {
      weekday: options.width ?? 'short',
      timeZone: UTC,
    }).format(date)
    return options.context === 'sentence' ? capitalize(label, locale) : label
  }

  const formatLocalDateDay: DateTimeFormatter['formatLocalDateDay'] = value => {
    const parts = parseLocalDate(value)
    return parts ? String(parts.day) : fallback
  }

  const formatWeekdayRange: DateTimeFormatter['formatWeekdayRange'] = (
    startIsoWeekday,
    endIsoWeekday,
    options = {},
  ) => {
    const startLabel = formatWeekday(startIsoWeekday, options)
    if (startLabel === fallback) return fallback
    if (startIsoWeekday === endIsoWeekday) return startLabel
    const endLabel = formatWeekday(endIsoWeekday, options)
    return endLabel === fallback ? startLabel : `${startLabel}${EN_DASH}${endLabel}`
  }

  const formatWeekdayTime: DateTimeFormatter['formatWeekdayTime'] = (
    isoWeekday,
    time,
    options = {},
  ) => {
    const weekdayLabel = formatWeekday(isoWeekday, {
      width: options.weekday ?? 'long',
      context: options.context,
    })
    if (weekdayLabel === fallback) return fallback
    const timeLabel = formatLocalTime(time)
    return timeLabel === fallback ? weekdayLabel : `${weekdayLabel} · ${timeLabel}`
  }

  const formatLocalDateWeekday: DateTimeFormatter['formatLocalDateWeekday'] = (
    value,
    options = {},
  ) => {
    const parts = parseLocalDate(value)
    if (!parts) return fallback
    const label = formatter(locale, {
      weekday: options.width ?? 'short',
      timeZone: UTC,
    }).format(asUtcCalendarDate(parts))
    return options.context === 'sentence' ? capitalize(label, locale) : label
  }

  const formatLocalDateWithWeekday: DateTimeFormatter['formatLocalDateWithWeekday'] = (
    value,
    options = {},
  ) => {
    const parts = parseLocalDate(value)
    if (!parts) return fallback
    const date = asUtcCalendarDate(parts)
    const weekday = formatter(locale, {
      weekday: options.weekday ?? 'short',
      timeZone: UTC,
    }).format(date)
    const normalizedWeekday = options.context === 'sentence' ? capitalize(weekday, locale) : weekday
    const dateLabel = formatCalendarDate(
      locale,
      parts,
      options.dateStyle ?? 'numeric',
      options.includeYear ?? true,
    )
    return `${normalizedWeekday}, ${dateLabel}`
  }

  const formatLocalDateRange: DateTimeFormatter['formatLocalDateRange'] = (
    startValue,
    endValue,
    options = {},
  ) => {
    const start = parseLocalDate(startValue)
    const end = parseLocalDate(endValue)
    if (!start && !end) return fallback
    if (!start) return formatLocalDate(endValue, {style: options.style ?? 'numeric'})
    if (!end) return formatLocalDate(startValue, {style: options.style ?? 'numeric'})
    if (options.style === 'long') return formatLongRange(locale, start, end)
    if (locale.toLocaleLowerCase().startsWith('pl')) return formatPolishNumericRange(start, end)
    return formatter(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: UTC,
    }).formatRange(asUtcCalendarDate(start), asUtcCalendarDate(end))
  }

  const formatLocalDateRangeWithWeekday: DateTimeFormatter['formatLocalDateRangeWithWeekday'] = (
    start,
    end,
    options = {},
  ) => {
    const weekdayLabel = formatLocalDateWeekday(start, {
      width: options.weekday ?? 'short',
      context: options.context,
    })
    if (weekdayLabel === fallback) return formatLocalDateRange(start, end, options)
    const rangeLabel = formatLocalDateRange(start, end, options)
    return rangeLabel === fallback ? weekdayLabel : `${weekdayLabel}, ${rangeLabel}`
  }

  const formatLocalTime: DateTimeFormatter['formatLocalTime'] = (value, options = {}) => {
    const parts = parseLocalTime(value)
    if (!parts) return fallback
    const base = `${parts.hour.toString().padStart(2, '0')}:${parts.minute.toString().padStart(2, '0')}`
    return options.seconds ? `${base}:${parts.second.toString().padStart(2, '0')}` : base
  }

  const formatDayBoundaryTime: DateTimeFormatter['formatDayBoundaryTime'] = value => {
    const normalized = value?.trim()
    return normalized === '24:00' || normalized === '24:00:00'
      ? '24:00'
      : formatLocalTime(value)
  }

  const formatLocalDateTime: DateTimeFormatter['formatLocalDateTime'] = (
    date,
    time,
    options = {},
  ) => {
    const dateLabel = formatLocalDate(date, {style: options.dateStyle ?? 'numeric'})
    if (dateLabel === fallback) return fallback
    const timeLabel = formatLocalTime(time, {seconds: options.seconds})
    return timeLabel === fallback ? dateLabel : `${dateLabel}, ${timeLabel}`
  }

  const formatTimeRange: DateTimeFormatter['formatTimeRange'] = (start, end, options = {}) => {
    const startLabel = formatLocalTime(start, options)
    if (startLabel === fallback) return fallback
    const endLabel = formatLocalTime(end, options)
    return endLabel === fallback ? startLabel : `${startLabel}${EN_DASH}${endLabel}`
  }

  const formatInstantTimeRange: DateTimeFormatter['formatInstantTimeRange'] = (
    start,
    end,
    options = {},
  ) => {
    const startLabel = formatInstantTime(start, options)
    if (startLabel === fallback) return fallback
    const endLabel = formatInstantTime(end, options)
    return endLabel === fallback ? startLabel : `${startLabel}${EN_DASH}${endLabel}`
  }

  const formatDayBoundaryTimeRange: DateTimeFormatter['formatDayBoundaryTimeRange'] = (start, end) => {
    const startLabel = formatDayBoundaryTime(start)
    if (startLabel === fallback) return fallback
    const endLabel = formatDayBoundaryTime(end)
    return endLabel === fallback ? startLabel : `${startLabel}${EN_DASH}${endLabel}`
  }

  const formatInstantDate: DateTimeFormatter['formatInstantDate'] = (value, options = {}) => {
    const date = parseInstant(value)
    if (!date) return fallback
    const style = options.style ?? 'numeric'
    return formatter(locale, {
      day: 'numeric',
      month: style === 'long' ? 'long' : '2-digit',
      ...(style === 'dayMonth' ? {} : {year: 'numeric'}),
      timeZone,
    }).format(date)
  }

  const formatInstantTime: DateTimeFormatter['formatInstantTime'] = (value, options = {}) => {
    const date = parseInstant(value)
    if (!date) return fallback
    return formatter(locale, {
      hour: '2-digit',
      minute: '2-digit',
      ...(options.seconds ? {second: '2-digit'} : {}),
      hourCycle: 'h23',
      timeZone,
    }).format(date)
  }

  const formatInstantDateTime: DateTimeFormatter['formatInstantDateTime'] = (
    value,
    options = {},
  ) => {
    const date = parseInstant(value)
    if (!date) return fallback
    const dateStyle = options.dateStyle ?? 'numeric'
    return formatter(locale, {
      day: 'numeric',
      month: dateStyle === 'long' ? 'long' : '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...(options.seconds ? {second: '2-digit'} : {}),
      hourCycle: 'h23',
      timeZone,
    }).format(date)
  }

  const dateKeyInZone: DateTimeFormatter['dateKeyInZone'] = value => {
    const date = parseInstant(value)
    if (!date) return null
    const parts = localDatePartsInZone('en-CA', date, timeZone)
    return parts ? localDateFromParts(parts) : null
  }

  const isSameDayInZone: DateTimeFormatter['isSameDayInZone'] = (first, second) => {
    const firstKey = dateKeyInZone(first)
    const secondKey = dateKeyInZone(second)
    return firstKey !== null && secondKey !== null && firstKey === secondKey
  }

  const formatInstantRange: DateTimeFormatter['formatInstantRange'] = (
    startValue,
    endValue,
    options = {},
  ) => {
    const start = parseInstant(startValue)
    const end = parseInstant(endValue)
    if (!start && !end) return fallback
    if (!start) return formatInstantDateTime(endValue, options)
    if (!end) return formatInstantDateTime(startValue, options)
    if (isSameDayInZone(start, end)) {
      return `${formatInstantDate(start)} · ${formatInstantTime(start, options)}${EN_DASH}${formatInstantTime(
        end,
        options,
      )}`
    }
    return `${formatInstantDateTime(start, options)} ${EN_DASH} ${formatInstantDateTime(end, options)}`
  }

  const formatInstantSession: DateTimeFormatter['formatInstantSession'] = value => {
    const start = parseInstant(value.start)
    const end = parseInstant(value.end)
    const startDate = dateKeyInZone(start)
    if (!start || !startDate) {
      return {timeLabel: fallback, dateLabel: '', combinedLabel: fallback, isCrossDay: false}
    }
    const endDate = dateKeyInZone(end)
    if (end && endDate && endDate !== startDate) {
      const dateOptions = {
        style: value.dateStyle ?? 'numeric',
        includeYear: value.includeYear ?? true,
      } as const
      const combinedLabel = `${formatLocalDate(startDate, dateOptions)} · ${formatInstantTime(
        start,
        {seconds: value.seconds},
      )} ${EN_DASH} ${formatLocalDate(endDate, dateOptions)} · ${formatInstantTime(
        end,
        {seconds: value.seconds},
      )}`
      return {timeLabel: combinedLabel, dateLabel: '', combinedLabel, isCrossDay: true}
    }
    const timeLabel = formatInstantTimeRange(start, end, {seconds: value.seconds})
    const dateLabel = formatLocalDateWithWeekday(startDate, {
      dateStyle: value.dateStyle ?? 'numeric',
      weekday: value.dateStyle === 'long' ? 'long' : 'short',
      includeYear: value.includeYear ?? true,
    })
    return {
      timeLabel,
      dateLabel,
      combinedLabel: `${timeLabel} · ${dateLabel}`,
      isCrossDay: false,
    }
  }

  const formatSession: DateTimeFormatter['formatSession'] = value => {
    const startDate = parseLocalDate(value.localDate)
    const endDate = parseLocalDate(value.localEndDate ?? value.localDate)
    const timeLabel = formatTimeRange(value.localStartTime, value.localEndTime)
    if (!startDate) {
      return {timeLabel: fallback, dateLabel: '', combinedLabel: fallback, isCrossDay: false}
    }
    const startDateString = localDateFromParts(startDate)
    if (endDate && !sameLocalDate(startDate, endDate)) {
      const endDateString = localDateFromParts(endDate)
      const dateOptions = {
        style: value.dateStyle ?? 'numeric',
        includeYear: value.includeYear ?? true,
      } as const
      const combinedLabel = `${formatLocalDate(startDateString, dateOptions)} · ${formatLocalTime(
        value.localStartTime,
      )} ${EN_DASH} ${formatLocalDate(endDateString, dateOptions)} · ${formatLocalTime(value.localEndTime)}`
      return {timeLabel: combinedLabel, dateLabel: '', combinedLabel, isCrossDay: true}
    }
    const dateLabel = formatLocalDateWithWeekday(startDateString, {
      dateStyle: value.dateStyle ?? 'numeric',
      weekday: value.dateStyle === 'long' ? 'long' : 'short',
      includeYear: value.includeYear ?? true,
    })
    return {
      timeLabel,
      dateLabel,
      combinedLabel: `${timeLabel} · ${dateLabel}`,
      isCrossDay: false,
    }
  }

  const formatOffsetSession: DateTimeFormatter['formatOffsetSession'] = value => {
    const start = offsetLocalParts(value.start)
    const end = offsetLocalParts(value.end)
    if (!start) {
      return {timeLabel: fallback, dateLabel: '', combinedLabel: fallback, isCrossDay: false}
    }
    return formatSession({
      localDate: start.localDate,
      localEndDate: end?.localDate ?? start.localDate,
      localStartTime: start.localTime,
      localEndTime: end?.localTime,
      dateStyle: value.dateStyle,
      includeYear: value.includeYear,
    })
  }

  const formatMonthTitle: DateTimeFormatter['formatMonthTitle'] = (value, options = {}) => {
    const match = value?.trim().match(YEAR_MONTH_PATTERN)
    if (!match) return fallback
    const year = Number(match[1])
    const month = Number(match[2])
    if (month < 1 || month > 12) return fallback
    const label = formatter(locale, {
      month: 'long',
      ...(options.includeYear === false ? {} : {year: 'numeric'}),
      timeZone: UTC,
    }).format(new Date(Date.UTC(year, month - 1, 1, 12)))
    return options.context === 'inline' ? label : capitalize(label, locale)
  }

  const formatLocalDateMonthTitle: DateTimeFormatter['formatLocalDateMonthTitle'] = (
    value,
    options = {},
  ) => {
    const parts = parseLocalDate(value)
    if (!parts) return fallback
    return formatMonthTitle(
      `${parts.year.toString().padStart(4, '0')}-${parts.month.toString().padStart(2, '0')}`,
      options,
    )
  }

  const formatRelative: DateTimeFormatter['formatRelative'] = value => {
    const date = parseInstant(value)
    const reference = now()
    if (!date || Number.isNaN(reference.getTime())) return fallback
    const seconds = Math.round((date.getTime() - reference.getTime()) / 1000)
    const absoluteSeconds = Math.abs(seconds)
    if (absoluteSeconds < 60) return relativeFormatter(locale).format(seconds, 'second')
    const minutes = Math.round(seconds / 60)
    if (Math.abs(minutes) < 60) return relativeFormatter(locale).format(minutes, 'minute')
    const hours = Math.round(minutes / 60)
    if (Math.abs(hours) < 24) return relativeFormatter(locale).format(hours, 'hour')
    const valueDate = dateKeyInZone(date)
    const referenceDate = dateKeyInZone(reference)
    const valueParts = parseLocalDate(valueDate)
    const referenceParts = parseLocalDate(referenceDate)
    if (!valueParts || !referenceParts) return fallback
    const days = Math.round(
      (asUtcCalendarDate(valueParts).getTime() - asUtcCalendarDate(referenceParts).getTime())
        / (24 * 60 * 60 * 1000),
    )
    if (Math.abs(days) < 7) return relativeFormatter(locale).format(days, 'day')
    return formatInstantDate(date)
  }

  return {
    locale,
    timeZone,
    fallback,
    formatLocalDateWithOptions,
    formatInstantWithOptions,
    formatLocalDateRangeWithOptions,
    formatInstantRangeWithOptions,
    formatLocalDate,
    formatLocalDateDay,
    formatLocalDateWeekday,
    formatLocalDateWithWeekday,
    formatLocalDateRange,
    formatLocalDateRangeWithWeekday,
    formatLocalTime,
    formatLocalDateTime,
    formatDayBoundaryTime,
    formatTimeRange,
    formatInstantTimeRange,
    formatDayBoundaryTimeRange,
    formatInstantDate,
    formatInstantTime,
    formatInstantDateTime,
    formatInstantRange,
    formatInstantSession,
    formatOffsetSession,
    formatSession,
    formatWeekday,
    formatWeekdayRange,
    formatWeekdayTime,
    formatMonthTitle,
    formatLocalDateMonthTitle,
    formatRelative,
    today: () => dateKeyInZone(now())!,
    dateKeyInZone,
    isSameDayInZone,
  }
}

/** Calendar arithmetic for navigation, never a pass-duration policy. */
export const addCalendarDays = (value: string, days: number): string => {
  const parsed = parseLocalDate(value)
  if (!parsed || !Number.isInteger(days)) throw new RangeError('Invalid calendar date or day count')
  const next = new Date(0)
  next.setUTCHours(12, 0, 0, 0)
  next.setUTCFullYear(parsed.year, parsed.month - 1, parsed.day + days)
  return `${String(next.getUTCFullYear()).padStart(4, '0')}-${String(next.getUTCMonth() + 1).padStart(2, '0')}-${String(next.getUTCDate()).padStart(2, '0')}`
}

export const formatLocalDateWithOptions = (value: string, locale: string, options: LocalDateFormatOptions): string => {
  const parsed = parseLocalDate(value)
  if (!parsed) throw new RangeError('Invalid calendar date')
  const anchor = new Date(0)
  anchor.setUTCHours(12, 0, 0, 0)
  anchor.setUTCFullYear(parsed.year, parsed.month - 1, parsed.day)
  return formatter(locale, {...options, timeZone: UTC}).format(anchor)
}

/** Option-preserving presentation still requires an explicit authoritative instant zone. */
export const formatInstantWithOptions = (
  value: Date | string | null | undefined, locale: string, timeZone: string, options: InstantFormatOptions,
): string => createDateTimeFormatter({locale, timeZone}).formatInstantWithOptions(value, options)

const rangeSeparator = (options?: Pick<RangeLabelOptions, 'separator' | 'collapseSharedParts'>): string =>
  options?.separator === 'compact' ? EN_DASH : options?.separator === 'hyphen' ? ' - ' : ` ${EN_DASH} `

/** Calendar range options preserve the current view's label width without instant conversion. */
const formatLocalDateRangeWithOptionsInLocale = (
  start: string | null | undefined, end: string | null | undefined, locale: string,
  options: LocalDateFormatOptions, endFallback = DEFAULT_DATE_TIME_FALLBACK,
  rangeOptions?: RangeLabelOptions, fallback = DEFAULT_DATE_TIME_FALLBACK,
): string => {
  const first = parseLocalDate(start), last = parseLocalDate(end)
  const endLabel = last ? formatLocalDateWithOptions(end!, locale, options) : endFallback
  if (first && last && rangeOptions?.collapseSameDate && sameLocalDate(first, last)) return endLabel
  const startOptions = first && last && rangeOptions?.omitRepeatedYear && first.year === last.year
    ? {...options, year: undefined} : options
  const startLabel = first ? formatLocalDateWithOptions(start!, locale, startOptions) : fallback
  return `${startLabel}${rangeSeparator(rangeOptions)}${endLabel}`
}

export const formatLocalDateRangeWithOptions = (
  start: string | null | undefined, end: string | null | undefined, locale: string,
  options: LocalDateFormatOptions, endFallback = DEFAULT_DATE_TIME_FALLBACK, rangeOptions?: RangeLabelOptions,
): string => formatLocalDateRangeWithOptionsInLocale(start, end, locale, options, endFallback, rangeOptions)

export const formatInstantRangeWithOptions = (
  start: Date | string | null | undefined, end: Date | string | null | undefined,
  locale: string, timeZone: string, options: InstantFormatOptions, endOptions?: InstantFormatOptions,
  rangeOptions?: Pick<RangeLabelOptions, 'separator' | 'collapseSharedParts'>,
): string => createDateTimeFormatter({locale, timeZone}).formatInstantRangeWithOptions(start, end, options, endOptions, rangeOptions)

export const calendarDaysBetween = (start: string, end: string): number => {
  const first = parseLocalDate(start), last = parseLocalDate(end)
  if (!first || !last) throw new RangeError('Invalid calendar date')
  return Math.round((asUtcCalendarDate(last).getTime() - asUtcCalendarDate(first).getTime()) / 86400000)
}

export const isoWeekdayForLocalDate = (value: string): number => {
  const date = parseLocalDate(value)
  if (!date) throw new RangeError('Invalid calendar date')
  return asUtcCalendarDate(date).getUTCDay() || 7
}

/** Clamp from the original anchor, so Jan 31 plus two months remains Mar 31. */
export const addCalendarMonths = (value: string, months: number): string => {
  const parts = parseLocalDate(value)
  if (!parts || !Number.isInteger(months)) throw new RangeError('Invalid calendar date or month count')
  const next = asUtcCalendarDate({...parts, day: 1})
  next.setUTCMonth(next.getUTCMonth() + months)
  const lastDay = new Date(next.getTime())
  lastDay.setUTCMonth(lastDay.getUTCMonth() + 1, 0)
  next.setUTCDate(Math.min(parts.day, lastDay.getUTCDate()))
  return localDateFromParts({year: next.getUTCFullYear(), month: next.getUTCMonth() + 1, day: next.getUTCDate()})
}

/** Native/web datetime-local fields require a machine value rather than a visible label. */
export const localDateTimeInputInZone = (value: Date | string | null | undefined, timeZone: string): string | null => {
  const dates = createDateTimeFormatter({locale: 'en', timeZone})
  const date = dates.dateKeyInZone(value)
  return date ? `${date}T${dates.formatInstantTime(value)}` : null
}

/** Resolve a wall-clock input using IANA rules; gaps fail, overlaps use the earlier instant. */
export const offsetDateTimeFromLocal = (localDate: string, localTime: string, timeZone: string): string => {
  const date = parseLocalDate(localDate)
  const time = parseLocalTime(localTime)
  if (!date || !time || !isIanaTimeZone(timeZone)) throw new RangeError('Invalid local date, time or time zone')
  const epoch = (parts: LocalDateParts & LocalTimeParts) => {
    const value = new Date(0)
    value.setUTCFullYear(parts.year, parts.month - 1, parts.day)
    value.setUTCHours(parts.hour, parts.minute, parts.second, 0)
    return value.getTime()
  }
  const requested = epoch({...date, ...time})
  const partsFormatter = formatter('en-CA', {timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'})
  const localEpoch = (instant: number) => {
    const parts = partsFormatter.formatToParts(new Date(instant))
    const part = (key: Intl.DateTimeFormatPartTypes) => Number(parts.find(value => value.type === key)?.value)
    return epoch({year: part('year'), month: part('month'), day: part('day'), hour: part('hour'), minute: part('minute'), second: part('second')})
  }
  const offsets = new Set([-48, -24, 0, 24, 48].map(hours => {
    const probe = requested + hours * 3_600_000
    return localEpoch(probe) - probe
  }))
  const candidates = [...offsets].map(offset => requested - offset).filter(instant => localEpoch(instant) === requested).sort((left, right) => left - right)
  if (!candidates.length) throw new RangeError('Local time does not exist in the selected time zone')
  const offsetMinutes = (requested - candidates[0]) / 60_000
  if (!Number.isInteger(offsetMinutes)) throw new RangeError('Sub-minute historical offsets are not supported by this input')
  const absolute = Math.abs(offsetMinutes)
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${localDateFromParts(date)}T${pad(time.hour)}:${pad(time.minute)}:${pad(time.second)}${offsetMinutes >= 0 ? '+' : '-'}${pad(Math.floor(absolute / 60))}:${pad(absolute % 60)}`
}

export type CalendarDateRange = Readonly<{start?: string | null; end?: string | null; timeZone: string}>

/** Coverage uses the day of the instant in the entitlement's immutable studio zone. */
export const coversCalendarDateRange = (range: CalendarDateRange, instant: string | Date): boolean => {
  if ((range.start && !parseLocalDate(range.start)) || (range.end && !parseLocalDate(range.end))) return false
  const day = createDateTimeFormatter({locale: 'en', timeZone: range.timeZone}).dateKeyInZone(instant)
  return Boolean(day && (!range.start || day >= range.start) && (!range.end || day <= range.end))
}
